import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Candidate selection
  selectedCandidates: [],

  // Questions state
  generatedQuestions: [], // Array of strings (10 questions)
  customQuestions: [], // Array of strings (recruiter-added questions)
  editedQuestions: {}, // { 0: {index, originalText, editedText, isEdited}, 1: {...}, ... }

  // Session IDs
  tempSessionId: null, // From /generate-questions (temporary)
  finalSessionId: null, // From /generate-interview-link (permanent)

  // Candidate information (single mode)
  candidateInfo: null, // { name, phone, role }

  // Interview links (after generation)
  interviewLinks: [], // Array of { candidate_id, interview_url, interview_id, session_id }

  // State flags
  isQuestionsFreezed: false,
  mode: null, // 'single' | 'bulk'
  isLoading: false,
  error: null,
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    // Candidate selection actions
    setSelectedCandidates: (state, action) => {
      state.selectedCandidates = action.payload;
    },

    toggleCandidateSelection: (state, action) => {
      const candidateId = action.payload;
      const index = state.selectedCandidates.indexOf(candidateId);
      if (index > -1) {
        state.selectedCandidates.splice(index, 1);
      } else {
        state.selectedCandidates.push(candidateId);
      }
    },

    selectAllCandidates: (state, action) => {
      state.selectedCandidates = action.payload; // Pass array of all candidate IDs
    },

    clearSelection: (state) => {
      state.selectedCandidates = [];
    },

    // Questions actions
    setGeneratedQuestions: (state, action) => {
      state.generatedQuestions = action.payload;
      // Initialize editedQuestions tracking
      state.editedQuestions = {};
      action.payload.forEach((question, index) => {
        state.editedQuestions[index] = {
          index,
          originalText: question,
          editedText: question,
          isEdited: false,
        };
      });
    },

    editQuestion: (state, action) => {
      const { index, newText } = action.payload;
      if (state.editedQuestions[index]) {
        state.editedQuestions[index].editedText = newText;
        state.editedQuestions[index].isEdited = newText !== state.editedQuestions[index].originalText;
      }
    },

    addCustomQuestion: (state, action) => {
      state.customQuestions.push(action.payload);
    },

    deleteCustomQuestion: (state, action) => {
      const index = action.payload;
      state.customQuestions.splice(index, 1);
    },

    resetQuestions: (state) => {
      state.generatedQuestions = [];
      state.customQuestions = [];
      state.editedQuestions = {};
      state.isQuestionsFreezed = false;
    },

    // Session ID actions
    setTempSessionId: (state, action) => {
      state.tempSessionId = action.payload;
    },

    setFinalSessionId: (state, action) => {
      state.finalSessionId = action.payload;
    },

    // Candidate info actions
    setCandidateInfo: (state, action) => {
      state.candidateInfo = action.payload;
    },

    // Interview links actions
    setInterviewLinks: (state, action) => {
      state.interviewLinks = action.payload;
      state.isQuestionsFreezed = true;
    },

    // Mode and state flag actions
    setMode: (state, action) => {
      state.mode = action.payload;
    },

    setQuestionsFreezed: (state, action) => {
      state.isQuestionsFreezed = action.payload;
    },

    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    // Reset entire interview state
    resetInterview: (state) => {
      return initialState;
    },
  },
});

export const {
  setSelectedCandidates,
  toggleCandidateSelection,
  selectAllCandidates,
  clearSelection,
  setGeneratedQuestions,
  editQuestion,
  addCustomQuestion,
  deleteCustomQuestion,
  resetQuestions,
  setTempSessionId,
  setFinalSessionId,
  setCandidateInfo,
  setInterviewLinks,
  setMode,
  setQuestionsFreezed,
  setIsLoading,
  setError,
  resetInterview,
} = interviewSlice.actions;

// Selectors
export const selectSelectedCandidates = (state) => state.interview.selectedCandidates;
export const selectSelectedCandidatesCount = (state) => state.interview.selectedCandidates.length;
export const selectGeneratedQuestions = (state) => state.interview.generatedQuestions;
export const selectCustomQuestions = (state) => state.interview.customQuestions;
export const selectEditedQuestions = (state) => state.interview.editedQuestions;
export const selectMode = (state) => state.interview.mode;
export const selectIsQuestionsFreezed = (state) => state.interview.isQuestionsFreezed;
export const selectTempSessionId = (state) => state.interview.tempSessionId;
export const selectFinalSessionId = (state) => state.interview.finalSessionId;
export const selectCandidateInfo = (state) => state.interview.candidateInfo;
export const selectInterviewLinks = (state) => state.interview.interviewLinks;
export const selectIsLoading = (state) => state.interview.isLoading;
export const selectError = (state) => state.interview.error;

// Composite selectors
export const selectHasUnsavedChanges = (state) => {
  const editedQuestions = state.interview.editedQuestions;
  const hasEditedQuestions = Object.values(editedQuestions).some((q) => q.isEdited);
  const hasCustomQuestions = state.interview.customQuestions.length > 0;
  return hasEditedQuestions || hasCustomQuestions;
};

export const selectAllQuestions = (state) => {
  const { generatedQuestions, editedQuestions, customQuestions } = state.interview;
  const editedGenerated = generatedQuestions.map(
    (_, index) => editedQuestions[index]?.editedText || generatedQuestions[index]
  );
  return [...editedGenerated, ...customQuestions];
};

export const selectChangeSummary = (state) => {
  const { editedQuestions, customQuestions } = state.interview;
  const editedCount = Object.values(editedQuestions).filter((q) => q.isEdited).length;
  const customCount = customQuestions.length;
  return { editedCount, customCount };
};

export default interviewSlice.reducer;
